import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { PendingAttachment } from '@/components/inbox/types'
import { filesToPendingAttachments } from '@/components/inbox/utils'

interface UseAttachmentInputOptions {
  setPendingAttachments: React.Dispatch<React.SetStateAction<PendingAttachment[]>>
  validateFileSize?: (files: File[]) => string | null
}

export function useAttachmentInput({
  setPendingAttachments,
  validateFileSize,
}: UseAttachmentInputOptions) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const addFiles = useCallback((files: File[]) => {
    if (files.length === 0) return

    if (validateFileSize) {
      const error = validateFileSize(files)
      if (error) {
        toast.error(error)
        return
      }
    }

    setPendingAttachments((prev) => [...prev, ...filesToPendingAttachments(files)])
  }, [validateFileSize, setPendingAttachments])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    addFiles(Array.from(files))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [addFiles])

  const handleRemoveAttachment = useCallback((id: string) => {
    setPendingAttachments((prev) => {
      const attachment = prev.find((a) => a.id === id)
      if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl)
      return prev.filter((a) => a.id !== id)
    })
  }, [setPendingAttachments])

  const handlePasteFiles = useCallback((files: File[]) => {
    addFiles(files)
  }, [addFiles])

  // Drag handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const currentTarget = e.currentTarget as HTMLElement
    if (!currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    addFiles(Array.from(e.dataTransfer.files))
  }, [addFiles])

  return {
    fileInputRef,
    isDragOver,
    handleFileSelect,
    handleRemoveAttachment,
    handlePasteFiles,
    dragHandlers: {
      onDragOver: handleDragOver,
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  }
}
